import { useRef, useEffect, useState, useMemo } from 'react';
import OLVectorLayer from 'ol/layer/Vector';
import { STYLE } from '../../style';
import { geoJSONToFeature } from '../../utils/geoJSONFeature';
import { vectorSourceFactory } from '../../utils/vectorSourceFactory';
import { styleFactory } from '../../utils/styleFactory';
import { useOLMap } from '../MapContext';
import { useLayerEffect } from './useLayerEffect';
import { createSymbolStyle, getUniqueValueStyle, getArcGISSymbolConfig, } from './arcGISSymbolFactory';
export const vectorLayerById = new Map();
export const FeatureArcGISLayer = (props) => {
    const map = useOLMap();
    const layerRef = useRef();
    const [layerIds, setLayerIds] = useState({});
    const [metadata, setMetadata] = useState(null);
    useLayerEffect(layerRef, props.layerOptions);
    useEffect(() => {
        const metdataUrl = props.sourceOptions.url + '/?f=json';
        fetch(metdataUrl, { credentials: 'same-origin' })
            .then((response) => response.json())
            .then((data) => setMetadata(data))
            .catch((err) => {
            console.error('ERROR FeatureArcGISLayer - call metadata', err);
        });
    }, [props.sourceOptions.url]);
    const sourceOptions = useMemo(() => {
        const configParams = props.sourceOptions.params;
        const queryUrl = typeof props.sourceOptions.url === 'string' ? props.sourceOptions.url : '';
        let url = queryUrl;
        if (queryUrl) {
            const agsUrl = new URL(`${queryUrl}/query`, window.location.href);
            const searchParams = agsUrl.searchParams;
            const params = {
                where: '1=1',
                outFields: '*',
                ...(configParams && typeof configParams === 'object' ? configParams : null),
                returnGeometry: true,
                outSR: map.getView().getProjection().getCode().slice(5), // remove EPSG:
                f: 'geojson',
            };
            for (const [key, value] of Object.entries(params)) {
                if (value !== null && value !== undefined) {
                    searchParams.set(key, String(value));
                }
            }
            url = agsUrl.href;
        }
        return { ...props.sourceOptions, layerId: props.id, url };
    }, [map, props.sourceOptions, props.id]);
    useEffect(() => {
        if (!metadata)
            return;
        const agsRendererConf = metadata.drawingInfo?.renderer;
        // set nonOL attribute for redux
        sourceOptions.layerId = props.id;
        let style = null;
        if (agsRendererConf) {
            const isUniqueValueRenderer = Array.isArray(agsRendererConf.uniqueValueInfos);
            const url = props.sourceOptions.url;
            if (isUniqueValueRenderer) {
                style = getUniqueValueStyle(metadata, url);
            }
            else {
                style = createSymbolStyle([getArcGISSymbolConfig(metadata, url)]);
            }
        }
        else if (props.style && !Array.isArray(props.style)) {
            style = styleFactory(props.style);
        }
        const layer = new OLVectorLayer({
            ...{ id: props.id },
            visible: props.layerOptions.visible,
            opacity: props.layerOptions.opacity ?? 1,
            minResolution: props.layerOptions.minResolution,
            maxResolution: props.layerOptions.maxResolution,
            zIndex: props.layerOptions.zIndex,
            source: vectorSourceFactory(sourceOptions),
            style: style ?? props.style ?? STYLE.DEFAULT,
        });
        layerRef.current = layer;
        vectorLayerById.set(props.id, layer);
        map.addLayer(layer);
        return () => {
            // unmount
            const vectorLayer = layerRef.current;
            if (vectorLayer) {
                vectorSourceFactory.map.delete(sourceOptions);
                vectorLayerById.delete(props.id);
                map.removeLayer(vectorLayer);
            }
        };
    }, [metadata, map, sourceOptions, props.id]);
    useEffect(() => {
        if (!metadata)
            return;
        const layerId = props.features.reduce((accumulator, currentValue) => {
            const layerId = currentValue.properties?.layerId;
            if (!layerId) {
                console.error('[FeatureArcGISLayer] - GeoJSONFeature has no LayerId');
            }
            else {
                accumulator[layerId] = true;
            }
            return accumulator;
        }, {});
        setLayerIds(layerId);
    }, [metadata, props.features]);
    // keep sync with redux state
    useEffect(() => {
        if (!layerIds[props.id] || !metadata)
            return;
        const olFeatures = props.features.map(geoJSONToFeature);
        const vectorLayer = layerRef.current;
        if (vectorLayer) {
            const currentInstanceOLFeatures = vectorLayer.getSource()?.getFeatures();
            if (!currentInstanceOLFeatures)
                return;
            const combineInstanceFeatures = olFeatures.map((newFeature) => {
                const currentInstanceFeature = currentInstanceOLFeatures.find((cf) => (newFeature.get('id') && newFeature.get('id') === cf.get('id')) ||
                    (newFeature.getId() && newFeature.getId() === cf.getId()));
                if (currentInstanceFeature) {
                    currentInstanceFeature.setProperties(newFeature.getProperties());
                    currentInstanceFeature.setGeometry(newFeature.getGeometry());
                    return currentInstanceFeature;
                }
                return newFeature;
            });
            const source = vectorSourceFactory(sourceOptions);
            source.clear();
            source.addFeatures(combineInstanceFeatures);
        }
    }, [metadata, props.features, props.id, sourceOptions, layerIds]);
    return null;
};
//# sourceMappingURL=FeatureArcGISLayer.js.map